'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-print',
    titleEs: 'Impresión Multifunción Corporativa',
    titleFr: 'Impression Multifonction d\'Entreprise',
    descriptionEs:
      'Soluciones de impresión, copiado y escaneo para entornos de alto volumen. Equipos multifunción de última generación con conectividad en red y gestión centralizada.',
    descriptionFr:
      'Solutions d\'impression, copie et numérisation pour les environnements à fort volume. Équipements multifonctions de dernière génération avec connectivité réseau et gestion centralisée.',
  },
  {
    icon: 'fas fa-file-contract',
    titleEs: 'Gestión Documental',
    titleFr: 'Gestion Documentaire',
    descriptionEs:
      'Digitalización, clasificación y archivo de documentos físicos. Flujos de trabajo automatizados que reducen el papel y mejoran la trazabilidad de la información.',
    descriptionFr:
      'Numérisation, classification et archivage de documents physiques. Flux de travail automatisés qui réduisent le papier et améliorent la traçabilité de l\'information.',
  },
  {
    icon: 'fas fa-tachometer-alt',
    titleEs: 'Monitoreo y Control de Impresión',
    titleFr: 'Surveillance et Contrôle d\'Impression',
    descriptionEs:
      'Plataformas de print management para controlar costos, asignar cuotas por usuario o departamento y generar reportes de uso detallados.',
    descriptionFr:
      'Plateformes de gestion d\'impression pour contrôler les coûts, attribuer des quotas par utilisateur ou département et générer des rapports d\'utilisation détaillés.',
  },
  {
    icon: 'fas fa-sync-alt',
    titleEs: 'Mantenimiento y Suministros',
    titleFr: 'Maintenance et Fournitures',
    descriptionEs:
      'Contratos de mantenimiento preventivo y correctivo, provisión de tóner, tinta y repuestos originales para garantizar la continuidad operativa de tu flota.',
    descriptionFr:
      'Contrats de maintenance préventive et corrective, fourniture de toner, d\'encre et de pièces d\'origine pour garantir la continuité opérationnelle de votre parc.',
  },
  {
    icon: 'fas fa-cloud-upload-alt',
    titleEs: 'Impresión en la Nube',
    titleFr: 'Impression dans le Cloud',
    descriptionEs:
      'Integración con plataformas cloud para imprimir desde cualquier dispositivo y ubicación, con autenticación segura y registro de actividad.',
    descriptionFr:
      'Intégration avec des plateformes cloud pour imprimer depuis n\'importe quel appareil et emplacement, avec authentification sécurisée et journal d\'activité.',
  },
  {
    icon: 'fas fa-chart-bar',
    titleEs: 'Auditoría y Optimización de Flota',
    titleFr: 'Audit et Optimisation du Parc',
    descriptionEs:
      'Análisis del parque de impresoras existente para identificar ineficiencias, reducir costos y proponer una arquitectura de impresión más eficiente y sostenible.',
    descriptionFr:
      'Analyse du parc d\'imprimantes existant pour identifier les inefficacités, réduire les coûts et proposer une architecture d\'impression plus efficace et durable.',
  },
];

const sectors = [
  { icon: 'fas fa-hospital', labelEs: 'Salud', labelFr: 'Santé' },
  { icon: 'fas fa-university', labelEs: 'Educación', labelFr: 'Éducation' },
  { icon: 'fas fa-building', labelEs: 'Gobierno', labelFr: 'Gouvernement' },
  { icon: 'fas fa-gavel', labelEs: 'Legal', labelFr: 'Juridique' },
  { icon: 'fas fa-store', labelEs: 'Retail', labelFr: 'Commerce' },
  { icon: 'fas fa-industry', labelEs: 'Industria', labelFr: 'Industrie' },
];

export default function ServiciosImpresionPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" /></div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block"><img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" /></div>
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Services d\'Impression' : 'Servicios de Impresión'}</span>
          </nav>
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fas fa-print" /><span>{isFr ? 'GESTION DOCUMENTAIRE' : 'GESTIÓN DOCUMENTAL'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Services d\'Impression' : 'Servicios de'}<br /><span style={{ color: '#1AEF0F' }}>{isFr ? '' : 'Impresión'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr ? 'Nous optimisons la gestion documentaire de votre organisation avec des solutions d\'impression intelligentes, le contrôle des coûts et la transition vers des environnements plus numériques et durables.' : 'Optimizamos la gestión documental de tu organización con soluciones de impresión inteligentes, control de costos y transición hacia entornos más digitales y sostenibles.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="https://calendar.app.google/qrMMuz1WHJJ4eoab8" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center overflow-hidden" style={{ border: '1px solid #1AEF0F', padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', color: '#1AEF0F', backgroundColor: 'transparent', transition: 'all 300ms ease-out' }}>
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">{isFr ? 'Demander un Diagnostic' : 'Solicitar Diagnóstico'}</span>
                  <span className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ backgroundColor: '#1AEF0F', zIndex: 0 }} />
                </a>
                <Link href="/#servicios" className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors" style={{ fontSize: '15px', padding: '14px 0' }}>
                  <i className="fas fa-arrow-left text-sm" />{isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>
            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: '+200', labelEs: 'Equipos gestionados', labelFr: 'Équipements gérés' },
                { value: '30%', labelEs: 'Reducción de costos', labelFr: 'Réduction des coûts' },
                { value: '99%', labelEs: 'Disponibilidad de flota', labelFr: 'Disponibilité du parc' },
                { value: '24/7', labelEs: 'Monitoreo remoto', labelFr: 'Surveillance à distance' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '24px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>{stat?.value}</div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>{isFr ? stat?.labelFr : stat?.labelEs}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.capabilitiesTitle}</h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>{isFr ? 'Solutions intégrales d\'impression et de gestion documentaire qui réduisent les coûts opérationnels et améliorent l\'efficacité des processus documentaires de votre organisation.' : 'Soluciones integrales de impresión y gestión documental que reducen costos operativos y mejoran la eficiencia de los procesos documentales de tu organización.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>{isFr ? cap?.titleFr : cap?.titleEs}</h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>{isFr ? cap?.descriptionFr : cap?.descriptionEs}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#f5f5f5' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-black mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>{s?.sectorsTitle}</h2>
          <div className="flex flex-wrap gap-4">
            {sectors?.map((sec, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4 bg-white" style={{ border: '1px solid #E2E2E2' }}>
                <i className={`${sec?.icon} text-[#1AEF0F]`} />
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#111' }}>{isFr ? sec?.labelFr : sec?.labelEs}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-black font-bold m-0" style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}>
            {isFr ? 'Vous souhaitez optimiser la gestion documentaire de votre entreprise ?' : '¿Quieres optimizar la gestión documental de tu empresa?'}
          </p>
          <Link href="/contact" className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0" style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}>
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">{s?.ctaBtnContact}</span>
            <span className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300" style={{ zIndex: 0 }} />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
